import type { BaseDocument } from '../mongo/base-document.interface';
import type { CarrierSummary } from '../carrier/carrier.interface';
import type { Contact } from '../contact';
import type { Eta } from '../eta/eta.interface';
import type { GeoPoint, Location, PostalAddress, RecordedCoordinates } from '../geolocation';
import type { Measurement, VolumeUnit, WeightUnit } from '../measurement/measurement.interface';
import type { OrderTypeSummary } from '../order-type/order-type.interface';
import type { PhoneNumber } from '../phone';
import type { SiteSummary } from '../site/site.interface';
import type { CustomerContact, CustomerSummary } from '../customer';
import type { SyncResource } from '../sync/sync-resource';
import type { TemplateSummary } from '../template/template.interface';
import type { UserSummary } from '../user/user.interface';
import { HISTORY_CREATED_EVENT_CODE } from '../history';

/** Max length of `Order.instruction` — enforced by `@MaxLength()` in the DTOs and by the KLAREO connector's silent truncation. */
export const ORDER_INSTRUCTION_MAX_LENGTH = 500;

export const TRANSPORT_TYPE = {
  DELIVERY: 'delivery',
  PICKUP: 'pickup',
} as const;
export type TransportType = (typeof TRANSPORT_TYPE)[keyof typeof TRANSPORT_TYPE];

/** Origin of an order's creation. Server-managed — never accepted on client create bodies. */
export const ORDER_SOURCE_TYPE = {
  IMPORT: 'import',
} as const;
export type OrderSourceType = (typeof ORDER_SOURCE_TYPE)[keyof typeof ORDER_SOURCE_TYPE];

export interface OrderSource {
  id?: string;
  type: OrderSourceType;
}

export const ORDER_STATUS = {
  ANOMALY: 'anomaly',
  ARRIVED_AT_DELIVERY: 'arrived_at_delivery',
  ARRIVED_AT_PICKUP: 'arrived_at_pickup',
  CANCELLED: 'cancelled',
  CARRIER_ACCEPTED: 'carrier_accepted',
  CARRIER_ASSIGNED: 'carrier_assigned',
  CARRIER_REJECTED: 'carrier_rejected',
  COMPLETED: 'completed',
  DRAFT: 'draft',
  DRIVER_ACCEPTED: 'driver_accepted',
  DRIVER_ASSIGNED: 'driver_assigned',
  DRIVING_TO_DELIVERY: 'driving_to_delivery',
  DRIVING_TO_PICKUP: 'driving_to_pickup',
  FAILED: 'failed',
  IN_TRANSIT: 'in_transit',
  NEW: 'new',
  PENDING: 'pending',
  PICKUP_COMPLETED: 'pickup_completed',
  WAITING_FOR_PICKUP: 'waiting_for_pickup',
} as const;
export type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS];

export function isOrderStatus(value: string): value is OrderStatus {
  return (Object.values(ORDER_STATUS) as string[]).includes(value);
}

export const ORDER_STATUS_REASON = {
  CANCELLED_BY_EXTERNAL_SYSTEM: 'cancelled_by_external_system',
  CANCELLED_BY_USER: 'cancelled_by_user',
  GEOCODING_FAILED: 'geocoding_failed',
  INVALID_TIME_WINDOW: 'invalid_time_window',
  METRICS_VALIDATION_FAILED: 'metrics_validation_failed',
  VALIDATION_PIPELINE_ERROR: 'validation_pipeline_error',
} as const;
export type OrderStatusReason = (typeof ORDER_STATUS_REASON)[keyof typeof ORDER_STATUS_REASON];

export const ORDER_STATUS_BUCKET = {
  PENDING: [ORDER_STATUS.PENDING],
  TODO: [
    ORDER_STATUS.ANOMALY,
    ORDER_STATUS.DRAFT,
    ORDER_STATUS.NEW,
    ORDER_STATUS.CARRIER_ASSIGNED,
    ORDER_STATUS.DRIVER_ASSIGNED,
    ORDER_STATUS.CARRIER_ACCEPTED,
    ORDER_STATUS.DRIVER_ACCEPTED,
    ORDER_STATUS.CARRIER_REJECTED,
    ORDER_STATUS.DRIVING_TO_PICKUP,
  ],
  IN_PROGRESS: [
    ORDER_STATUS.ARRIVED_AT_PICKUP,
    ORDER_STATUS.WAITING_FOR_PICKUP,
    ORDER_STATUS.PICKUP_COMPLETED,
    ORDER_STATUS.IN_TRANSIT,
    ORDER_STATUS.DRIVING_TO_DELIVERY,
    ORDER_STATUS.ARRIVED_AT_DELIVERY,
  ],
  DONE: [ORDER_STATUS.COMPLETED, ORDER_STATUS.FAILED, ORDER_STATUS.CANCELLED],
} as const;

/** Statuses an order can never move out of again — shared by cancellability checks and status-sync writes. */
export const TERMINAL_ORDER_STATUSES: readonly OrderStatus[] = [ORDER_STATUS.COMPLETED, ORDER_STATUS.CANCELLED];

export function isOrderCancellable(status: OrderStatus): boolean {
  return !TERMINAL_ORDER_STATUSES.includes(status);
}

/**
 * Known `HistoryEntry.code` values for the order history feed — the whitelist backing
 * `apps/pharos-back-deliveries/src/order/order-history.events.ts` (plus the platform-emitted
 * `created`). `HistoryEntry.code` itself stays a loose `string` (resource-agnostic, see
 * `history/history.interface.ts`); this const object is the closed view over it for order
 * consumers, used for i18n key building and per-code formatting.
 */
export const ORDER_HISTORY_EVENT_CODE = {
  CREATED: HISTORY_CREATED_EVENT_CODE,
  CUSTOMER_FIRST_NAME: 'customerFirstName',
  CUSTOMER_LAST_NAME: 'customerLastName',
  DELIVERY_ADDRESS: 'deliveryAddress',
  DELIVERY_TIMESLOT: 'deliveryTimeslot',
  HANDLING_UNITS: 'handlingUnits',
  ITEMS: 'items',
  ORDER_VALUE: 'orderValue',
  PICKUP_TIMESLOT: 'pickupTimeslot',
  REQUESTED_TIME_WINDOW: 'requestedTimeWindow',
  STATUS: 'status',
  TAGS: 'tags',
  TOTAL_QUANTITY: 'totalQuantity',
  TOTAL_VOLUME: 'totalVolume',
  TOTAL_WEIGHT: 'totalWeight',
} as const;
export type OrderHistoryEventCode = (typeof ORDER_HISTORY_EVENT_CODE)[keyof typeof ORDER_HISTORY_EVENT_CODE];

export interface Amount {
  currency: string;
  value: number;
}

/** Generic barcode standards recognized across connectors — not specific to any one of them. */
export const BARCODE_TYPES = {
  EAN13: 'EAN13',
  SSCC: 'SSCC',
} as const;
export type BarcodeType = (typeof BARCODE_TYPES)[keyof typeof BARCODE_TYPES];

export interface Barcode {
  type: string;
  value: string;
}

export interface Computable<T> {
  data: T;
  isComputed: boolean;
}

/** Persisted, resolved delivery address (snapshot of the matched customer address). */
export interface DeliveryAddress {
  location: Location;
  originalAddress: string;
}

/** Raw client input on order creation. */
export interface CreateDeliveryAddress extends Partial<PostalAddress> {
  coordinates?: GeoPoint;
  /** Raw string address; wins over structured fields when present. */
  full?: string;
  phone?: PhoneNumber;
}

/** Postal keys forming the address identity; touching any puts a patch under the partial-address merge guard. */
export const ADDRESS_IDENTITY_KEYS = [
  'city',
  'country',
  'houseNumber',
  'postalCode',
  'street',
] as const satisfies readonly (keyof CreateDeliveryAddress)[];
export type AddressIdentityKey = (typeof ADDRESS_IDENTITY_KEYS)[number];

export interface Delivery {
  address?: DeliveryAddress;
  /** `null` is a legitimate persisted removal signal (see order-update-fields.ts), not just "unset". */
  contact?: Contact | null;
  durationTime?: number;
  proofOf?: ProofOf[];
  /**
   * Transient: present only until `address` is resolved (the same write that sets `address`
   * unsets this field); kept on anomaly/legacy orders.
   */
  requestedAddress?: CreateDeliveryAddress;
  site?: SiteSummary;
  timeSlot?: TimeSlot;
}

export type CreateDelivery = Omit<Delivery, 'address' | 'requestedAddress' | 'site'> & {
  address?: CreateDeliveryAddress;
  siteCode?: string;
};

export const HANDLING_UNIT_TEMPERATURES = {
  DRY: 'dry',
  FRESH: 'fresh',
  FROZEN: 'frozen',
} as const;
export type HandlingUnitTemperature = (typeof HANDLING_UNIT_TEMPERATURES)[keyof typeof HANDLING_UNIT_TEMPERATURES];

export function isHandlingUnitTemperature(value: string): value is HandlingUnitTemperature {
  return (Object.values(HANDLING_UNIT_TEMPERATURES) as string[]).includes(value);
}

export interface HandlingUnit {
  code?: Barcode;
  /**
   * Server-minted, stable line identifier. Creation input never carries it (`CreateHandlingUnit`);
   * update input may echo one back as an optional match hint (`UpdateHandlingUnit`) so a
   * read-modify-write client keeps the same id across a patch — an id that does not match an
   * existing line is ignored and a fresh one is minted for that line.
   */
  id: string;
  quantity: number;
  sortIndex?: number;
  temperature?: HandlingUnitTemperature;
  type: string;
  volume?: Measurement<VolumeUnit>;
  weight?: Measurement<WeightUnit>;
}

/** Raw client input for a handling-unit line on creation — `id` is always minted server-side. */
export type CreateHandlingUnit = Omit<HandlingUnit, 'id'>;

/**
 * Update-time input for a handling-unit line: same as `CreateHandlingUnit`, plus an optional `id`
 * — a match hint only, used to keep a line's id stable across a patch (see `HandlingUnit.id`).
 * Never trusted blindly: an id the server doesn't recognize is ignored, never persisted as-is.
 */
export type UpdateHandlingUnit = CreateHandlingUnit & { id?: string };

export interface Item {
  code?: Barcode;
  label: string;
  price?: Price;
  quantity: number;
  sortIndex?: number;
  type?: string;
  volume?: Measurement<VolumeUnit>;
  weight?: Measurement<WeightUnit>;
}

export interface Pickup {
  /** `null` is a legitimate persisted removal signal (see order-update-fields.ts), not just "unset". */
  contact?: Contact | null;
  durationTime: number;
  proofOf?: ProofOf[];
  site: SiteSummary;
  timeSlot?: TimeSlot;
}
export type CreatePickup = Omit<Pickup, 'durationTime' | 'site'> & { durationTime?: number; siteCode: string };

export interface Price extends Amount {
  taxRate?: number;
}

export interface ProofOf {
  code: string;
  value: string;
}

export interface TimeSlot {
  end: Date;
  start: Date;
}

export interface OrderAssignment {
  carrier: CarrierSummary;
  driver?: UserSummary;
}

export interface Order extends BaseDocument {
  assignedTo?: OrderAssignment[];
  // Stamped atomically with the transition to `completed` (same $set as the status write);
  // never re-stamped afterward.
  completedAt?: Date;
  customer: CustomerSummary;
  delivery: Delivery;
  /**
   * Stamped by the ETA engine (`pharos-back-deliveries`) with the `Eta` of the stop carrying this
   * order's `transportType`-matching job (delivery order -> delivery job's stop, pickup order ->
   * pickup job's stop — the same leg `requestedTimeWindow` binds to). Unset when that job is
   * removed from its queue (task removed from queue, cancelled-task dismissal, driver/carrier
   * unassignment) — never cleared by a status transition, so a completed/failed order keeps its
   * last stamped value.
   */
  eta?: Eta;
  externalId?: string;
  handlingUnits?: HandlingUnit[];
  initialTimeWindow?: TimeSlot;
  instruction?: string;
  items?: Item[];
  orderType: OrderTypeSummary;
  orderValue?: Amount;
  pickup: Pickup;
  referenceNumber: string;
  requestedCarrier?: CarrierSummary;
  requestedTimeWindow?: TimeSlot;
  shipper: TemplateSummary;
  /** Origin of the order's creation (e.g. file import). Server-managed — absent means direct API/connector creation. */
  source?: OrderSource;
  spaceId: string;
  siteId: string;
  status: OrderStatus;
  // Driver position at the most recent driver-declared status transition; null when that action carried no GPS.
  statusCoordinates?: RecordedCoordinates | null;
  statusReason?: string;
  statusReasonMessage?: string;
  tags?: string[];
  totalHandlingUnitQuantity?: Computable<number>;
  totalVolume?: Computable<Measurement<VolumeUnit>>;
  totalWeight?: Computable<Measurement<WeightUnit>>;
  transportType: TransportType;
}

export type CreateOrder = Omit<
  Order,
  | 'assignedTo'
  | 'completedAt'
  | 'createdAt'
  | 'customer'
  | 'deletedAt'
  | 'delivery'
  | 'eta'
  | 'handlingUnits'
  | 'id'
  | 'initialTimeWindow'
  | 'orderType'
  | 'pickup'
  | 'referenceNumber'
  | 'requestedCarrier'
  | 'shipper'
  | 'source'
  | 'spaceId'
  | 'siteId'
  | 'status'
  | 'statusCoordinates'
  | 'statusReason'
  | 'statusReasonMessage'
  | 'tenantId'
  | 'totalHandlingUnitQuantity'
  | 'totalVolume'
  | 'totalWeight'
  | 'updatedAt'
> & {
  customer: CustomerContact;
  delivery: CreateDelivery;
  handlingUnits?: CreateHandlingUnit[];
  orderTypeCode: string;
  pickup: CreatePickup;
  /** Auto-generated (`${prefix}-${seq}`, one global cross-tenant sequence) by `OrderService` when omitted on manual creation. Required on ingest/import. */
  referenceNumber?: string;
  requestedCarrierCode?: string;
  shipperCode: string;
  siteCode: string;
  totalHandlingUnitQuantity?: number;
  totalVolume?: Measurement<VolumeUnit>;
  totalWeight?: Measurement<WeightUnit>;
};

// ─── Sync resource descriptor (declaration-merged with the Order interface) ──

/**
 * Runtime descriptor for Order collection synchronization.
 * Use `@SyncSource(Order)` on a service to publish create/update/delete events.
 */
export const Order: SyncResource<Order> = {
  collection: 'orders',
  name: 'order',
};
